import type { TradeSectionData, VehicleFuelEconomy, FuelEconomyData, TradeInInfo } from '../types';
import { initialState } from '../types';

/**
 * Find MPG (combined) from fuel economy data by VIN
 * @param vin - Vehicle VIN to look up
 * @param vehicles - Array of vehicle fuel economy data
 * @returns Combined MPG as string, or empty string if not found
 */
export const findMpgByVin = (vin: string | undefined, vehicles: VehicleFuelEconomy[] | undefined): string => {
    if (!vin || !vehicles) return '';
    const match = vehicles.find((v) => v.vin === vin && v.ok && v.fuelEconomy);
    const combinedMpg = match?.fuelEconomy?.combined;
    return typeof combinedMpg === 'number' && combinedMpg > 0 ? String(combinedMpg) : '';
};

/**
 * Build display name from trade-in vehicle info (e.g., "2023 Honda Accord")
 * @param tradeIn - Trade-in info object
 * @returns Formatted trade name or "Trade-In" as default
 */
export const getTradeName = (tradeIn: TradeInInfo): string =>
    [tradeIn.year, tradeIn.make, tradeIn.model].filter(Boolean).join(' ') || 'Trade-In';

/**
 * Build trade section data with prefilled tradeInMpg from fuel economy API.
 * Note: newVehicleMpg is set dynamically by the library based on active vehicle tab.
 * @param tradeIn - Trade-in info object
 * @param index - Index for generating unique ID
 * @param fuelEconomyData - Fuel economy API response
 * @returns TradeSectionData for the trade-in
 */
export const buildTradeSection = (
    tradeIn: TradeInInfo,
    index: number,
    fuelEconomyData: FuelEconomyData | null
): TradeSectionData => ({
    id: `trade-${index}`,
    name: getTradeName(tradeIn),
    vin: tradeIn.vin,
    values: {
        ...initialState.formValues,
        tradeInMpg: findMpgByVin(tradeIn.vin, fuelEconomyData?.data?.tradeinVehiclesVin)
    }
});
